import {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {PhoneHQGlyph, PhoneHQLoader, PhoneHQConfirmDialog} from 'ch3-ui-lib';
import {
    useCancelEnsAlertMutation,
    useCreateEnsAlertTemplateFromAlertMutation,
    useGetEnsAlertQuery,
} from "../../../store/features/ens/ensService";
import {showToast} from "../../../store/features/Notifications/notificationsSlice";
import {EnsAlertStatus} from "../../../store/features/ens/types";
import {StatusBadge} from "../components/StatusBadge";
import {TypeBadge} from "../components/TypeBadge";
import {useEnsGroupNames} from "../useEnsGroupNames";
import {EscalationHistogram} from "./EscalationHistogram";
import {TimelineTab} from "./TimelineTab";
import {RecipientsTab} from "./RecipientsTab";
import {OverrideTab} from "./OverrideTab";
import {ENS_ALERT_REFRESH_INTERVAL_MS} from "../utils";

const TERMINAL_STATUSES: EnsAlertStatus[] = ['CONFIRMED', 'FAILED', 'CANCELLED'];

type TabKey = 'timeline' | 'recipients' | 'override';

interface Props {
    publicId: string | null;
    onClose: () => void;
}

const shortPublicId = (publicId: string) =>
    publicId.length > 8 ? `${publicId.slice(0, 8)}…` : publicId;

const TabButton = ({active, label, onClick}: { active: boolean; label: string; onClick: () => void }) => (
    <button
        type="button"
        onClick={onClick}
        className={`h-9 flex-1 border-b-2 text-[13px] font-semibold transition-colors text-center ${
            active
                ? 'border-brand text-brand-ink'
                : 'border-transparent text-ink-muted hover:text-ink'
        }`}
    >
        {label}
    </button>
);

export const AlertDetailsPanel = ({publicId, onClose}: Props) => {
    const skip = !publicId;
    const dispatch = useDispatch();
    const {data: alert, isLoading, isError} = useGetEnsAlertQuery(publicId ?? '', {
        skip,
        pollingInterval: skip ? 0 : ENS_ALERT_REFRESH_INTERVAL_MS,
    });
    const groupNames = useEnsGroupNames();

    const handlePause = () => {
        dispatch(showToast({description: 'Pausing escalation is not available yet.', type: 'info'}));
    };
    const [cancelAlert] = useCancelEnsAlertMutation();
    const [createTemplateFromAlert, {isLoading: savingTemplate}] = useCreateEnsAlertTemplateFromAlertMutation();
    const [activeTab, setActiveTab] = useState<TabKey>('timeline');
    const [savedTemplateAlertIds, setSavedTemplateAlertIds] = useState<Set<string>>(() => new Set());
    const [cancelOpen, setCancelOpen] = useState(false);

    useEffect(() => {
        setActiveTab('timeline');
    }, [publicId]);

    if (!publicId) {
        return (
            <div
                className="hidden h-full w-full max-w-[400px] shrink-0 flex-col items-center justify-center border-l border-line bg-surface px-6 text-center min-[1920px]:flex"
                role="complementary"
                aria-label="Alert details"
            >
                <PhoneHQGlyph name="ads_click" size={28} className="mb-2 text-ink-disabled"/>
                <div className="text-sm text-ink-muted">Select an alert to view its details.</div>
            </div>
        );
    }

    const handleCancel = async (confirmed: boolean) => {
        if (!confirmed || !alert) return;
        try {
            await cancelAlert(alert.summary.publicId).unwrap();
            onClose();
        } catch {
            // toast handled in service
        }
    };

    const handleSaveTemplate = async () => {
        if (!alert) return;
        try {
            await createTemplateFromAlert(alert.summary.publicId).unwrap();
            setSavedTemplateAlertIds((previous) => {
                const next = new Set(previous);
                next.add(alert.summary.publicId);
                return next;
            });
        } catch {
            // toast handled in service
        }
    };

    return (
        <>
            <button
                type="button"
                aria-label="Close alert details"
                onClick={onClose}
                className="absolute inset-0 z-10 bg-scrim min-[1920px]:hidden"
            />
            <div
                className="absolute bottom-0 right-0 top-0 z-20 flex h-full w-[calc(100%-24px)] max-w-[420px] flex-col border-l border-line bg-surface shadow-[-12px_0_32px_rgba(0,0,0,0.14)] min-[1920px]:static min-[1920px]:z-auto min-[1920px]:max-w-[400px] min-[1920px]:shrink-0 min-[1920px]:shadow-none"
                role="complementary"
                aria-label="Alert details"
            >
            <div className="flex items-start justify-between gap-2 border-b border-line px-4 py-3.5">
                <div className="min-w-0 flex-1">
                    {alert && (
                        <div className="mb-1.5 flex flex-wrap items-center gap-2">
                            <StatusBadge status={alert.summary.status}/>
                            <TypeBadge type={alert.summary.type}/>
                        </div>
                    )}
                    <h3 className="truncate text-[15px] font-bold leading-tight text-ink">
                        {alert?.summary.name ?? '—'}
                    </h3>
                    {alert && (() => {
                        const groupName = alert.targetGroupIds
                            .map((id) => groupNames.get(id))
                            .find((name): name is string => Boolean(name));
                        const recipients = `${alert.summary.targetCount} ${alert.summary.targetCount === 1 ? 'recipient' : 'recipients'}`;
                        return (
                            <div className="mt-1 truncate text-[11px] text-ink-muted">
                                <span className="font-mono">{shortPublicId(alert.summary.publicId)}</span>
                                {groupName && <> · {groupName}</>}
                                {' · '}{recipients}
                            </div>
                        );
                    })()}
                </div>
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-surface-muted text-ink-muted transition-colors hover:bg-surface-muted hover:text-ink"
                >
                    <PhoneHQGlyph name="close" size={16}/>
                </button>
            </div>

            {isLoading && (
                <div className="flex flex-1 items-center justify-center">
                    <PhoneHQLoader/>
                </div>
            )}

            {!isLoading && (isError || !alert) && (
                <div className="px-5 py-10 text-center text-sm text-ink-muted">
                    Alert not found.
                </div>
            )}

            {alert && (() => {
                const isTerminal = TERMINAL_STATUSES.includes(alert.summary.status);
                const templateSaved = savedTemplateAlertIds.has(alert.summary.publicId);
                return (
                    <>
                        <div className="flex shrink-0 items-center gap-2 border-b border-line px-4 py-2.5">
                            {!isTerminal && (
                                <>
                                    <PhoneHQConfirmDialog
                                        open={cancelOpen}
                                        onOpenChange={setCancelOpen}
                                        title="Cancel alert"
                                        cancelLabel="Keep running"
                                        confirmLabel="Yes, cancel alert"
                                        tone="danger"
                                        onConfirm={() => {
                                            setCancelOpen(false);
                                            void handleCancel(true);
                                        }}
                                        trigger={
                                            <button
                                                type="button"
                                                className="flex h-9 flex-1 items-center justify-center gap-1.5 rounded-lg border border-danger-tint bg-danger-tint text-xs font-semibold text-danger-ink transition-colors hover:bg-danger-tint"
                                            >
                                                <PhoneHQGlyph name="block" size={15}/>
                                                Cancel alert
                                            </button>
                                        }
                                    >
                                        Cancelling will stop further escalation for all pending recipients.
                                    </PhoneHQConfirmDialog>
                                    <button
                                        type="button"
                                        onClick={handlePause}
                                        title="Pause escalation (keep the alert open)"
                                        className="flex h-9 items-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-xs font-semibold text-ink-secondary transition-colors hover:border-fill-subtle"
                                    >
                                        <PhoneHQGlyph name="pause" size={15}/>
                                        Pause
                                    </button>
                                </>
                            )}
                            <button
                                type="button"
                                onClick={handleSaveTemplate}
                                disabled={savingTemplate || templateSaved}
                                title="Save as template"
                                className={`flex h-9 items-center justify-center gap-1.5 rounded-lg border border-line bg-surface px-3 text-xs font-semibold text-ink-secondary transition-colors hover:border-brand-focus hover:text-brand-ink disabled:opacity-50 ${isTerminal ? 'flex-1' : ''}`}
                            >
                                <PhoneHQGlyph name={templateSaved ? 'check' : 'bookmark_add'} size={15}/>
                                {isTerminal ? (templateSaved ? 'Saved as template' : (savingTemplate ? 'Saving…' : 'Save as template')) : ''}
                            </button>
                        </div>

                        {alert.summary.message && (
                            <div className="border-b border-line bg-surface-muted px-4 py-3">
                                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-muted">
                                    Message
                                </div>
                                <div className="whitespace-pre-line text-sm text-ink-secondary">
                                    {alert.summary.message}
                                </div>
                            </div>
                        )}

                        <EscalationHistogram
                            levels={alert.levels}
                            recipients={alert.recipients}
                            notifications={alert.notifications}
                        />

                        <div className="flex shrink-0 border-b border-line px-2">
                            <TabButton
                                active={activeTab === 'timeline'}
                                label="Timeline"
                                onClick={() => setActiveTab('timeline')}
                            />
                            <TabButton
                                active={activeTab === 'recipients'}
                                label={`Recipients (${alert.recipients.length})`}
                                onClick={() => setActiveTab('recipients')}
                            />
                            <TabButton
                                active={activeTab === 'override'}
                                label="Override"
                                onClick={() => setActiveTab('override')}
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto items-center">
                            {activeTab === 'timeline' && (
                                <TimelineTab
                                    summary={alert.summary}
                                    levels={alert.levels}
                                    notifications={alert.notifications}
                                />
                            )}
                            {activeTab === 'recipients' && (
                                <RecipientsTab recipients={alert.recipients} notifications={alert.notifications}/>
                            )}
                            {activeTab === 'override' && (
                                <OverrideTab
                                    publicId={alert.summary.publicId}
                                    recipients={alert.recipients}
                                    isTerminal={isTerminal}
                                />
                            )}
                        </div>
                    </>
                );
            })()}
            </div>
        </>
    );
};
